import * as React from 'react';

import * as UI from 'core/ui';
import { ValidationErrors, Validator } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Permission, Res, BuyerCustomerDTO, AppointmentForEditionDTO, AppointmentStatus } from 'core/api';

import { Command } from 'pos/common';

import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as Images from 'pos/Images';

import { AppointmentFacade } from 'pos/facades';
import { Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { legacyControlsFor } from 'core/ui-next/controls';

const CustomerControl = ({ customer, onChangeCustomer }: { customer: Nullable<BuyerCustomerDTO>, onChangeCustomer: () => void }) => {
	const name = customer && customer.Id !== 0 ? customer.FiscalName : Res.Web.EDIT_APPOINTMENTS_CUSTOMER_DATA_NO_CUSTOMER_LBL();
	const cif = customer && customer.Id !== 0 ? customer.Cif : '';
	const discount = customer && customer.Id !== 0 && customer.DiscountRate !== 0
		? UI.Format.asPercentage(customer.DiscountRate * 100)
		: '';
	const lastSales = customer && customer.Id !== 0
		? <a className='link m-l-md' onClick={async () => await Command.execute('showRecentSalesByCustomer', customer)}>{Res.Web.EDIT_APPOINTMENTS_CUSTOMER_SALES_BTN()}</a>
		: null;
	const changeButtonText = customer && customer.Id !== 0
		? Res.Web.EDIT_APPOINTMENTS_CHANGE_CUSTOMER_BTN()
		: Res.Web.EDIT_APPOINTMENTS_SELECT_CUSTOMER_BTN();

	return <>
		<p className='font-bold text-16'>{name}</p>
		{cif ? <p>{cif}</p> : null}
		{discount ? <p>{Res.Web.EDIT_APPOINTMENTS_CUSTOMER_DATA_CUSTOMER_DISCOUNT_LBL(discount)}</p> : null}
		<UI.Button buttonStyle='info' outline onClick={onChangeCustomer}>{changeButtonText}</UI.Button>
		{lastSales}
	</>;

};

const maxGuests = 9999;

export type Props = Modal.Actions<void> & { appointment: AppointmentForEditionDTO };

export type EditAppointmentModel = AppointmentForEditionDTO & {
	Time: Date;
};

export class EditAppointmentScreen extends PosScreenClassComponent<Props, EditAppointmentModel> {

	protected form = React.createRef<UI.Form<EditAppointmentModel>>();

	protected getScreenId(): string {
		return 'EDIT_APPOINTMENT';
	}

	protected getTitle(): string {
		return Res.Web.EDIT_APPOINTMENTS_TITLE();
	}

	protected getImage(): string | SvgIcon {
		return Images.ShowAppointments;
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.createAppointment)
		];
	}

	createInitialState(props: Props): Nullable<EditAppointmentModel> {
		return {
			...props.appointment,
			// Separo fecha y hora para poder bindear por separado
			Time: props.appointment.Date
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	private createAppointment = async () => {
		const isValid = await this.form.current!.updateModel();

		if (!isValid)
			return;

		if (!this.state.Customer || this.state.Customer.Id === 0) {
			await Message.show(Res.Web.EDIT_APPOINTMENTS_MUST_SET_CUSTOMER_LBL());
			return;
		}

		this.state.Date.setHours(this.state.Time.getHours(), this.state.Time.getMinutes(), 0, 0);

		await AppointmentFacade.save(this.state);
		this.close();
	};

	private changeCustomer = async () => {
		const customerResult = await Command.execute('searchCustomerByText');
		if (customerResult.ok) {
			this.setState({ Customer: customerResult.value });
		}
	};

	validate = (state: EditAppointmentModel): ValidationErrors => {
		const errors: ValidationErrors = {};

		if (state.Guests !== null && state.Guests > maxGuests)
			ValidationErrors.add(errors, 'Guests', Res.Msg.MSG_MAX_GUESTS(maxGuests));

		return errors;
	};

	protected getContent(): React.ReactNode {
		const { Form, TimeBox, IntBox, ComboBox, TextBox } = UI.controlsFor<EditAppointmentModel>();
		const { DatePicker } = legacyControlsFor<EditAppointmentModel>();

		return <Flex.Horizontal className='p-sm'>
			<Flex.Item size='*'>
				<p className='text-muted text-26 m-b-lg'>
					<i className='fa fa-id-card-o'></i> {Res.Web.EDIT_APPOINTMENTS_CUSTOMER_DATA_GRP()}
				</p>
				<CustomerControl customer={this.state.Customer} onChangeCustomer={this.changeCustomer} />
			</Flex.Item>
			<Flex.Item size='2*' style={{ overflowX: 'hidden' /* Necesario porque en PC sale una barra de scroll por el margin-right -15px del form-group */ }}>
				<p className='text-muted text-26 m-b-lg'>
					<i className='fa fa-calendar-check-o'></i> {Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_DATA_GRP()}
				</p>
				<Form ref={this.form} model={this.state} validator={Validator.create(this.validate)} >
					<DatePicker label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_DATE_LBL()} linkTo={x => x.Date} required />
					<TimeBox label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_TIME_LBL()} linkTo={x => x.Time} required />
					<IntBox label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_GUESTS_LBL()} linkTo={x => x.Guests} allowNegative={false} />
					<ComboBox label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_LOCATION_LBL()} linkTo={x => x.Location} placeholder={Res.Web.EDIT_APPOINTMENTS_LOCATION_PLACEHOLDER()} options={this.state.ExistingLocations} format={option => UI.Format.asLocationDescription({ FriendlyName: '', Location: option })} />
					<ComboBox label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_STATUS_LBL()} linkTo={x => x.Status} options={this.state.ExistingStatuses} format={AppointmentStatus.toString} required />
					<TextBox label={Res.Web.EDIT_APPOINTMENTS_APPOINTMENT_NOTES_LBL()} linkTo={x => x.Notes} lines={5} />
				</Form>
			</Flex.Item>
		</Flex.Horizontal>;
	}
}

declare global {
	interface CommandType {
		editAppointment: (appointment: AppointmentForEditionDTO) => Promise<Result>
	}
}

Command.register('editAppointment', Preconditions.and(Preconditions.ensure(Permission.Appointment_CreateOrModify), Preconditions.ensureIsNotLite), async (appointment: AppointmentForEditionDTO): Promise<Result> => {
	return await Modal.show(EditAppointmentScreen, { appointment: appointment });
});
